import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, RefreshCw, Building, Copy, ShieldCheck } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const empty = { name: "", slug: "", domain: "" };

export default function Orgs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [revealed, setRevealed] = useState(null); // { org_id, scim_token }

  const load = useCallback(async () => {
    try { const { data } = await api.get("/orgs"); setItems(data); }
    catch (err) { console.error("orgs load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.domain) delete payload.domain;
      const { data } = await api.post("/orgs", payload);
      toast.success("Org created");
      if (data?.scim_token) setRevealed({ org_id: data.org_id, scim_token: data.scim_token });
      setOpen(false); setForm(empty); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this org? SCIM provisioning will stop immediately.")) return;
    try { await api.delete(`/orgs/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rotate = async (id) => {
    if (!window.confirm("Rotate the SCIM token for this org? The old token will stop working.")) return;
    try {
      const { data } = await api.post(`/orgs/${id}/rotate-scim-token`);
      setRevealed({ org_id: id, scim_token: data?.scim_token });
      toast.success("SCIM token rotated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Clipboard unavailable"); }
  };

  return (
    <Layout title="Orgs (SCIM)" subtitle="MULTI-TENANCY">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="orgs-count">{items.length} ORGS</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-org-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> NEW ORG</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Building className="w-4 h-4 text-cyan-400" /> Create Org</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="org-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div><L>Slug</L><Input data-testid="org-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Domain (optional)</L><Input data-testid="org-domain-input" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-org-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {revealed && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4 mb-4" data-testid="scim-reveal">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400 mb-2">// SCIM TOKEN — shown once</div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-xs text-zinc-200 break-all flex-1 bg-zinc-950/60 p-2 rounded-sm border border-zinc-800">{revealed.scim_token}</code>
            <Button size="sm" variant="ghost" onClick={() => copy(revealed.scim_token)} className="gap-1.5"><Copy className="w-3.5 h-3.5" />Copy</Button>
            <Button size="sm" variant="ghost" onClick={() => setRevealed(null)} className="text-zinc-400">Dismiss</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="orgs-list">
        {items.map((o) => (
          <div key={o.org_id} data-testid={`org-card-${o.org_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold tracking-tight">{o.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{o.org_id} · /{o.slug}</div>
              </div>
              {o.domain && <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-cyan-500/40 text-cyan-400 bg-cyan-500/10 rounded-sm">{o.domain}</span>}
            </div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
              <Button size="sm" variant="ghost" data-testid={`rotate-org-${o.org_id}`} onClick={() => rotate(o.org_id)} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5" />Rotate SCIM</Button>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-org-${o.org_id}`} onClick={() => del(o.org_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
            <AuthPolicyPanel orgId={o.org_id} />
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no orgs yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }

function AuthPolicyPanel({ orgId }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/orgs/${orgId}/auth-policy`)
      .then(({ data }) => { if (alive) setP(data); })
      .catch(() => { if (alive) setP({ require_mfa: false, require_idp_mfa: false, accept_idp_mfa: true }); });
    return () => { alive = false; };
  }, [orgId]);

  const update = async (patch) => {
    if (!p) return;
    const next = { ...p, ...patch };
    setP(next);
    setBusy(true);
    try {
      await api.put(`/orgs/${orgId}/auth-policy`, {
        require_mfa: !!next.require_mfa,
        require_idp_mfa: !!next.require_idp_mfa,
        accept_idp_mfa: !!next.accept_idp_mfa,
      });
      toast.success("Auth policy updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      // Roll back optimistic update
      setP(p);
    } finally { setBusy(false); }
  };

  if (!p) return null;
  return (
    <div className="mt-3 pt-3 border-t border-zinc-800" data-testid={`auth-policy-${orgId}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-2">
        <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" /> Auth Policy
      </div>
      <Toggle label="Require MFA"
        hint="Every login (password or SSO) must produce a second factor."
        checked={p.require_mfa} disabled={busy}
        onChange={(v) => update({ require_mfa: v })} testid={`pol-require-mfa-${orgId}`} />
      <Toggle label="Demand IdP MFA"
        hint="Add acr_values to the SSO authorize URL so Google/Microsoft/Okta perform MFA."
        checked={p.require_idp_mfa} disabled={busy}
        onChange={(v) => update({ require_idp_mfa: v })} testid={`pol-idp-mfa-${orgId}`} />
      <Toggle label="Accept IdP MFA"
        hint="Off = require local TOTP even after IdP MFA (defense-in-depth)."
        checked={p.accept_idp_mfa} disabled={busy}
        onChange={(v) => update({ accept_idp_mfa: v })} testid={`pol-accept-${orgId}`} />
    </div>
  );
}

function Toggle({ label, hint, checked, disabled, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0 flex-1">
        <div className="text-xs text-zinc-200">{label}</div>
        <div className="text-[10px] font-mono text-zinc-500 leading-snug">{hint}</div>
      </div>
      <Switch checked={!!checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
